import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { setUser, clearUser, setLoading } from "../store/slice/userSlice";

function toReduxUser(apiUser) {
  if (!apiUser) return null;
  return {
    isAuthenticated: !!apiUser.isAuthenticated,
    id: apiUser.id || apiUser.userId || "",
    firstname: apiUser.firstname || apiUser.firstName || "",
    lastname: apiUser.lastname || apiUser.lastName || "",
    firstName: apiUser.firstname || apiUser.firstName || "",
    lastName: apiUser.lastname || apiUser.lastName || "",
    email: apiUser.email || "",
    mobile: apiUser.mobile || "",
    orgId: apiUser.orgId || "",
    clientRole: apiUser.clientRole || apiUser.role || "machine_operator",
    role: apiUser.role || apiUser.clientRole || "machine_operator",
  };
}

export function useAuth() {
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user?.user);
  const isLoadingFromStore = useSelector((state) => state.user?.isLoading);

  const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

  const {
    data: apiUser,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [`${backendUrl}/api/auth/me`],
    queryFn: async () => {
      try {
        const res = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: "include",
        });
        if (res.status === 401) return null;
        if (!res.ok) throw new Error("Failed to fetch session");
        return res.json();
      } catch (err) {
        return null;
      }
    },
    retry: false,
  });

  useEffect(() => {
    dispatch(setLoading(isLoading));
    if (isLoading) return;

    if (apiUser) {
      dispatch(setUser(toReduxUser(apiUser)));
    } else if (apiUser === null || isError) {
      dispatch(clearUser());
    }
  }, [apiUser, isLoading, isError, dispatch]);

  const safeUser = apiUser ? toReduxUser(apiUser) : null;
  const effectiveUser = safeUser || reduxUser;

  return {
    user: effectiveUser,
    isLoading: isLoading || isLoadingFromStore,
    isAuthenticated: !!effectiveUser?.isAuthenticated,
    role: effectiveUser?.clientRole || effectiveUser?.role,
  };
}
